// FlixPlayerGestureDetector.jsx
// This component detects and delegates keyboard hotkeys, click, double clicks, tap and double taps gesture to handlers from props.
// It also push every hotkeys/gestures to Actions Stack, and renders elements in the stack as animations.
// The pushed actions will automatically pops after a period of time.
// Double click/tap actions will revert last action pushed to the stack.

import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Observable, fromEvent } from 'rxjs';
import { timeInterval, map, filter, share } from 'rxjs/operators';
import styled, { css, keyframes } from 'styled-components';

import { getIsOnMobile } from '../resource/getUserAgent.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/trackingConstants.js';
import createTrackingEvent from '../resource/createTrackingEvent.js';

import { flixPlayerZIndex } from '../style/variables.js';
import BigPlayIcon from '../../img/ic-play-60.svg';
import BigPauseIcon from '../../img/ic-pause-60.svg';
import FastForwardIcon from '../../img/ic-fastforward.svg';
import AudioOffIcon from '../../img/ic-audio-off.svg';
import AudioOnIcon from '../../img/ic-audio-on.svg';
import VolumeIcon from '../../img/ic_audio_32px.svg';
import VolumeMuteIcon from '../../img/ic_mute_32px.svg';

const ACTION_ANIMATION_DURATION_MILLISECONDS = 500; // TODO: remote config
export const MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS = 300; // TODO: remote config

/** @typedef {import('rxjs').Subscription} Subscription */

class FlixPlayerGestureDetector extends React.PureComponent {
  state = {
    actions: [],
  };

  actionId = 0;

  timeouts = [];

  /** @type {import('react').RefObject<HTMLElement>} */
  ref = React.createRef();
  /** @type {Subscription} */ resizeSubscription = null;
  /** @type {Subscription} */ keyDownSubscription = null;
  /** @type {Subscription} */ mobileSingleClickSubscription = null;
  /** @type {Subscription} */ mobileDoubleClickSubscription = null;
  /** @type {Subscription} */ desktopSingleClickSubscription = null;
  /** @type {Subscription} */ desktopDoubleClickSubscription = null;
  /** @type {DOMRect} */
  boundingClientRect = null;

  isOnMobile = getIsOnMobile();
  rxClickNext = null;

  init = () => {
    const { isEnabled } = this.props;
    if (!isEnabled) {
      return;
    }
    // Update boundingClientReact
    this.boundingClientRect = this.ref.current.getBoundingClientRect();

    this.resizeSubscription = fromEvent(document, 'resize').subscribe(
      this.handleResize
    );
    this.keyDownSubscription = fromEvent(window, 'keydown').subscribe(
      this.handleKeyDown
    );

    // Configure observable
    /** @type {Observable<MouseEvent>} */
    const clickStream = new Observable(observer => {
      this.rxClickNext = observer.next.bind(observer);
    });

    // transform and subscribe to observable
    const clickIntervalStream = clickStream.pipe(timeInterval(), share());
    const singleClickStream = clickIntervalStream.pipe(
      filter(e => e.interval > MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS),
      map(timeInterval => timeInterval.value)
    );
    const doubleClickStream = clickIntervalStream.pipe(
      filter(e => e.interval <= MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS),
      map(timeInterval => timeInterval.value)
    );
    if (this.isOnMobile) {
      this.mobileSingleClickSubscription = singleClickStream.subscribe(
        this.handleMobileSingleClickEvent
      );
      this.mobileDoubleClickSubscription = doubleClickStream.subscribe(
        this.handleMobileDoubleClickEvent
      );
    } else {
      this.desktopSingleClickSubscription = singleClickStream.subscribe(
        this.handleDesktopSingleClickEvent
      );
      this.desktopDoubleClickSubscription = doubleClickStream.subscribe(
        this.handleDesktopDoubleClickEvent
      );
    }
  };

  componentDidMount() {
    this.init();
  }

  componentDidUpdate(prevProps) {
    const { isEnabled } = this.props;
    if (isEnabled !== prevProps.isEnabled) {
      this.init();
    }
  }

  componentWillUnmount() {
    this.resizeSubscription?.unsubscribe();
    this.keyDownSubscription?.unsubscribe();
    this.mobileSingleClickSubscription?.unsubscribe();
    this.mobileDoubleClickSubscription?.unsubscribe();
    this.desktopSingleClickSubscription?.unsubscribe();
    this.desktopDoubleClickSubscription?.unsubscribe();
    // Clear all remaining timeouts
    this.timeouts.forEach(clearTimeout);
  }

  pushAction(action) {
    // Push action to stack
    this.setState({
      actions: [{ ...action, id: this.actionId++ }, ...this.state.actions],
    });
    // Pop action after animation ends
    this.timeouts.push(
      setTimeout(() => {
        this.popAction();
        // remove timeout handle from this.timeouts array
        this.timeouts.pop();
      }, 2000) // TODO: remote config
    );
  }

  popAction() {
    this.setState({
      actions: this.state.actions.slice(0, this.state.actions.length - 1),
    });
  }

  revertAction() {
    // To prevent setState from race condition, revert must be awaited
    return new Promise(resolve => {
      clearTimeout(this.timeouts[0]);
      this.timeouts.splice(0, 1);
      this.setState(
        {
          actions: this.state.actions.slice(1),
        },
        resolve
      );
    });
  }

  handleMobileSingleClickEvent = () => {
    const { onToggleMobileControls } = this.props;
    onToggleMobileControls?.();
    this.pushAction({ type: 'mobileShowControls' });
  };

  /** @param {MouseEvent} e */
  handleMobileDoubleClickEvent = async e => {
    const position =
      (e.clientX - this.boundingClientRect.x) / this.boundingClientRect.width;
    if (position < 1 / 3) {
      const { onMobileFastForward } = this.props;
      onMobileFastForward?.(-10);
      await this.revertAction();
      this.pushAction({ type: 'mobileFastForward', data: -10 });
    } else if (position < 2 / 3) {
      const { onToggleMobileControls } = this.props;
      onToggleMobileControls?.();
      this.pushAction({ type: 'mobileShowControls' });
    } else {
      const { onMobileFastForward } = this.props;
      onMobileFastForward?.(10);
      await this.revertAction();
      this.pushAction({ type: 'mobileFastForward', data: 10 });
    }
  };

  /** @param {MouseEvent} e */
  handleDesktopSingleClickEvent = e => {
    const { onTogglePlay, isPaused } = this.props;
    // Desktop
    onTogglePlay?.();
    this.trackClickEvent({
      event: e,
      buttonId: this.props.isPaused
        ? ButtonId.Detail.PlayerControlsPlay
        : ButtonId.Detail.PlayerControlsPause,
    });
    this.pushAction({
      type: isPaused ? 'desktopPlay' : 'desktopPause',
    });
  };

  /** @param {MouseEvent} e */
  handleDesktopDoubleClickEvent = async e => {
    const { onTogglePlay, onToggleFullscreen } = this.props;
    onTogglePlay?.();
    this.trackClickEvent({
      event: e,
      buttonId: this.props.isPaused
        ? ButtonId.Detail.PlayerControlsPlay
        : ButtonId.Detail.PlayerControlsPause,
    });

    onToggleFullscreen?.();
    this.trackClickEvent({
      event: e,
      buttonId: ButtonId.Detail.PlayerControlsFullscreen,
    });
    await this.revertAction();
    this.pushAction({ type: 'desktopToggleFullscreen' });
  };

  /** @param {KeyboardEvent} e */
  shouldHandleKeys(e) {
    // should not handle keys if user is holding any of the meta keys
    if (e.ctrlKey || e.metaKey || e.altKey) return false;

    // Check focused element

    // Focused on nothing (browser default)
    if (document.activeElement.tagName === 'BODY') return true;

    const containerNode = this.props.containerRef.current;
    if (!containerNode) return false;

    // Find if ancestor is focused
    let currentNode = document.activeElement;
    while (currentNode) {
      if (currentNode === containerNode) return true;
      currentNode = currentNode.parentElement;
    }
    return false;
  }

  /** @param {KeyboardEvent} e */
  handleKeyDown = e => {
    if (!this.shouldHandleKeys(e)) return;

    let isEventHandled = true;
    switch (e.key.toLowerCase()) {
      case 'arrowleft': {
        this.props.onFastForward?.(-5);
        this.pushAction({ type: 'desktopFastForward', data: -5 });
        break;
      }
      case 'arrowright': {
        this.props.onFastForward?.(5);
        this.pushAction({ type: 'desktopFastForward', data: 5 });
        break;
      }
      case 'arrowup': {
        const nextVolume = this.props.onAdjustVolume?.(1);
        if (typeof nextVolume === 'number') {
          this.pushAction({ type: 'desktopVolume', data: nextVolume });
        }
        break;
      }
      case 'arrowdown': {
        const nextVolume = this.props.onAdjustVolume?.(-1);
        if (typeof nextVolume === 'number') {
          this.pushAction({ type: 'desktopVolume', data: nextVolume });
        }
        break;
      }
      case ' ':
        this.props.onTogglePlay?.();
        this.trackClickEvent({
          event: e,
          buttonId: this.props.isPaused
            ? ButtonId.Detail.PlayerControlsPlay
            : ButtonId.Detail.PlayerControlsPause,
        });
        this.pushAction({
          type: this.props.isPaused ? 'desktopPlay' : 'desktopPause',
        });
        break;
      case 'f':
        this.props.onToggleFullscreen?.();
        this.trackClickEvent({
          event: e,
          buttonId: ButtonId.Detail.PlayerControlsFullscreen,
        });
        this.pushAction({ type: 'desktopToggleFullscreen' });
        break;
      case 'm':
        this.pushAction({
          type: this.props.isMuted ? 'desktopUnmute' : 'desktopMute',
        });
        this.trackClickEvent({
          event: e,
          buttonId: this.props.isMuted
            ? ButtonId.All.ButtonMuted
            : ButtonId.All.ButtonVolume,
        });
        this.props.onToggleMute?.();
        break;
      default:
        if (/[0-9]/.test(e.key)) {
          const percentage = parseFloat(e.key) / 10;
          this.props.onJumpToPercentage?.(percentage);
          this.pushAction({
            type: 'desktopJumpToPercentage',
            data: percentage,
          });
        } else {
          isEventHandled = false;
        }
        break;
    }
    if (isEventHandled) e.preventDefault();
  };

  handleResize = () => {
    this.boundingClientRect = this.ref.current.getBoundingClientRect();
  };

  renderActionAnimation = action => {
    switch (action.type) {
      case 'desktopPlay':
        return (
          <DesktopAction key={action.id}>
            <img src={BigPlayIcon} />
          </DesktopAction>
        );
      case 'desktopPause':
        return (
          <DesktopAction key={action.id}>
            <img src={BigPauseIcon} />
          </DesktopAction>
        );
      case 'desktopMute':
        return (
          <DesktopAction key={action.id}>
            <img src={AudioOffIcon} />
          </DesktopAction>
        );
      case 'desktopUnmute':
        return (
          <DesktopAction key={action.id}>
            <img src={AudioOnIcon} />
          </DesktopAction>
        );
      case 'desktopFastForward':
        return (
          <DesktopAction key={action.id}>
            <DesktopFastForward isRewind={action.data < 0}>
              <img src={FastForwardIcon} />
              {`${Math.abs(action.data)} ${this.props.t('second_unit', {
                ns: TranslationNamespace.POST,
              })}`}
            </DesktopFastForward>
          </DesktopAction>
        );
      case 'desktopVolume':
        return (
          <DesktopAction key={action.id}>
            <DesktopVolume>
              <img src={action.data === 0 ? VolumeMuteIcon : VolumeIcon} />
              {`${Math.round(action.data * 100)}%`}
            </DesktopVolume>
          </DesktopAction>
        );
      case 'mobileFastForward':
        return (
          <MobileFastForward key={action.id} isRewind={action.data < 0}>
            <MobileFastForwardContent>
              <img src={FastForwardIcon} />
              {`${Math.abs(action.data)} ${this.props.t('second_unit', {
                ns: TranslationNamespace.POST,
              })}`}
            </MobileFastForwardContent>
          </MobileFastForward>
        );
      default:
        return null;
    }
  };

  trackClickEvent = ({ event, buttonId }) => {
    window.dispatchEvent(
      new CustomEvent('globalclick', {
        detail: {
          buttonId,
          ...this.props.trackingPayload,
          ...createTrackingEvent(event.currentTarget),
          srcTarget: 'component',
        },
      })
    );
  };

  render() {
    const { children } = this.props;
    const { actions } = this.state;
    return (
      <StyledFlixPlayerGestureDetector
        ref={this.ref}
        onClick={this.rxClickNext}
        onDoubleClick={() => {}} // Fix iOS 13.4.1 gestures bug.
      >
        {children}
        {actions.map(this.renderActionAnimation).reverse()}
      </StyledFlixPlayerGestureDetector>
    );
  }
}

FlixPlayerGestureDetector.propTypes = {
  children: PropTypes.node,
  trackingPayload: PropTypes.object,
  containerRef: PropTypes.object.isRequired,
  isPaused: PropTypes.bool.isRequired,
  isMuted: PropTypes.bool.isRequired,
  isEnabled: PropTypes.bool.isRequired,
  onTogglePlay: PropTypes.func,
  onToggleFullscreen: PropTypes.func,
  onToggleMute: PropTypes.func,
  onJumpToPercentage: PropTypes.func,
  onToggleMobileControls: PropTypes.func,
  onMobileFastForward: PropTypes.func,
  onFastForward: PropTypes.func,
  onAdjustVolume: PropTypes.func,
  t: PropTypes.func,
};

const DesktopActionKeyframes = keyframes`
  from  {
    opacity: 1;
    transform: scale(1);
  }
  to  {
    opacity: 0;
    transform: scale(2);
  }
`;

const DesktopActionAnimation = css`
  ${DesktopActionKeyframes}
`;

const MobileFastForwardKeyframes = keyframes`
  from  {
    opacity: 1;
  }
  to  {
    opacity: 0;
  }
`;

const MobileFastForwardAnimation = css`
  ${MobileFastForwardKeyframes}
`;

const StyledFlixPlayerGestureDetector = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${flixPlayerZIndex.gestureDetector};
`;

const DesktopAction = styled.div`
  background-color: rgba(25, 25, 25, 0.5);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: ${flixPlayerZIndex.actionAnimation};
  width: 82px;
  height: 82px;
  margin-top: -41px;
  margin-left: -41px;
  animation: ${ACTION_ANIMATION_DURATION_MILLISECONDS}ms forwards
    ${DesktopActionAnimation} ease-in;

  & > img {
    width: 60px;
    height: 60px;
  }
`;

const DesktopFastForward = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #ffffff;
  font-size: 11px;
  line-height: 1;
  text-align: center;

  & > img {
    width: 32px;
    height: 32px;
    margin-bottom: 4px;
    ${({ isRewind }) => (isRewind ? 'transform: rotate(180deg);: ' : '')}
  }
`;

const DesktopVolume = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #ffffff;
  font-size: 11px;
  line-height: 1;
  text-align: center;

  & > img {
    width: 32px;
    height: 32px;
    margin-bottom: 4px;
  }
`;

const MobileFastForward = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: ${flixPlayerZIndex.actionAnimation};
  width: 155px;
  background-image: linear-gradient(
    to ${({ isRewind }) => (isRewind ? 'left' : 'right')},
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0.6)
  );
  animation: 1000ms forwards ${MobileFastForwardAnimation} ease-in;
  display: flex;
  ${({ isRewind }) => (isRewind ? 'left: 0;' : 'right: 0;')}
  ${({ isRewind }) =>
    isRewind ? 'padding-left: 32px;' : 'padding-right: 32px;'}
  flex-direction: column;
  justify-content: center;
  align-items: ${({ isRewind }) => (isRewind ? 'flex-start' : 'flex-end')};
  & img {
    width: 32px;
    height: 32px;
    ${({ isRewind }) => (isRewind ? 'transform: rotate(180deg);: ' : '')}
    margin-bottom: 4px;
  }
`;

const MobileFastForwardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #ffffff;
  font-size: 11px;
  line-height: 1;
  text-align: center;
`;

export default withTranslation()(FlixPlayerGestureDetector);
